import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInitializeMap } from "./hooks/useInitializeMap";
import { GaiaMapRibbon } from "./components/GaiaMapRibbon";
import "./GaiaMap.css";
import { Box, Typography } from "@mui/material";
import { GaiaMapContext, GaiaMapProvider } from "./hooks/useGaiaMapContext";
import { GaiaMapControls } from "./components/GaiaMapControls";
import { GaiaMapPrintPreview } from "./components/GaiaMapPrintPreview";
import L, { Layer, Polyline } from "leaflet";
import { useMapHistory } from "./hooks/useMapHistory";
import { GetBoundaryRequest, GetBoundaryResponse } from "@shared/mirage/core/contracts/GetBoundaryRequest";
import { GeometryProjector } from "@shared/mirage/utils/GeometryProjector";
import { MultiLineString, Feature, Polygon, LineString } from "@turf/turf";
import { useUndoRedo } from "./hooks/useUndoRedo";
import { useFeatureGroupToWorkingGeometry } from "./hooks/useFeatureGroupToWorkingGeometry";
import { MapViewHelper, ColorPalette } from "../MapView/MapViewHelper";
import MapMarkersPane from "../MapView/components/MapMarkersPane";
import { MapMarkersGroup } from "../MapView/MapView";
import { useGaiaMapTheme } from "./hooks/useGaiaMapTheme";
import { GetFeatureInfoRequest, GetFeatureInfoResponse, WmsCapabilitiesResponse, WmsLayer } from "@shared/mirage/services/WmsCapabilities";
import { GetWmsCapabilitiesRequest } from "@shared/mirage/services/WmsCapabilities";
import Field, { MirageOnChangeEvent } from "../regularField/Field";
import { Legend } from "../MapView/components/MapViewLegend";
import { EarthlightMapBoxSize, PrintMapRequest } from "@shared/mirage/services/IEarthlightService";
import { useBoolean } from "@mirage/hooks/useBoolean";
import useAlertService from "@mirage/hooks/useAlertService";
import "./WMS/NonTiledLayer";
import "./GaiaMap.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

export interface TileLayerOptions {
    layerUrl: string;
    attribution?: string;
}

export interface GaiaMapWmsLayer {
    url?: string | null;
    showLegend?: boolean;
    getWmsCapabilities?: (req: GetWmsCapabilitiesRequest) => Promise<WmsCapabilitiesResponse>;
    showLayers?: "all" | string[];
    onShownLayersChanged?: (layers: string[]) => void;
}

export type EnabledToolTypes = "polygon" | "polyline" | "point" | "delete" | "circle" | "selection" | "inspect" | "edit";

export interface GaiaMapProps {
    tileLayer?: TileLayerOptions;
    onClone?: (req: GetBoundaryRequest) => Promise<GetBoundaryResponse>;
    center?: L.LatLngExpression | null;
    /**
     * @description GeoJSON string representation in 27700 projection
     */
    geometryJson?: string | null;
    /**
     * @description When true, disables all edit actions (drawing, selecting, deleting).
     */
    readOnly?: boolean;
    /**
     * @description Specifies which tool types should be enabled
     */
    enabledToolTypes?: EnabledToolTypes[];
    singleGeometryMode?: boolean;
    /**
     * @description Callback function that is called when the geometry changes
     * @param geometry - The geometry of the map in 27700 projection
     * @param lastDrawnGeometry - The last drawn geometry of the map FeatureCollection<Geometry, GeoJsonProperties> reprojected to 27700
     */
    onChange?: (geometry: string | null, lastDrawnGeometry: string | null) => void;
    /**
     * @description Callback function that is called when a selection is made
     * @param drawnPolygon - The drawn polygon in GeoJSON format (WGS84)
     * @param actionType - The type of selection action ("select" | "deselect")
     */
    onSelect?: (drawnPolygon: GeoJSON.Feature<GeoJSON.Polygon>, actionType: "select" | "deselect") => void;
    clusterMarkersGroup?: MapMarkersGroup;
    /**
     * @description Custom buttons to be added to the ribbon
     */
    customRibbonButtons?: React.ReactElement[];
    disableUndoRedo?: boolean;
    disableAcceptDrawing?: boolean;
    /**
     * @description Override default colors for map elements
     */
    colorPalette?: ColorPalette;
    wms?: GaiaMapWmsLayer;
    getFeatureInfo?: (req: GetFeatureInfoRequest) => Promise<GetFeatureInfoResponse>;
    onSnapshotRequest?: (req: PrintMapRequest) => Promise<void>;
    onMapBoxSizeRequest?: () => Promise<EarthlightMapBoxSize>;
    pointCustomIcon?: L.Icon;
    /**
     * @description Height of the map in pixels
     */
    height?: number;
    /**
     * @description Zoom level of the map
     */
    zoom?: number;
}
export const GAIA_DRAW_MODE = {
    MARKER: "marker",
    POLYLINE: "polyline",
    POLYGON: "polygon",
    SUBTRACT: "subtract",
    CLONE: "clone",
    DELETE: "delete",
    CIRCLE: "circle",
    INSPECT: "inspect",
    RULER: "ruler",
    SELECT: "select",
    DESELECT: "deselect",
    EDIT: "edit",
} as const;
export type GaiaDrawModeType = typeof GAIA_DRAW_MODE[keyof typeof GAIA_DRAW_MODE] | null;
export type MapWmsLayer = {
    layer: WmsLayer;
    selected: boolean;
};
export const TOOL_TYPE_TO_DRAW_MODES = {
    polygon: [GAIA_DRAW_MODE.CLONE, GAIA_DRAW_MODE.POLYGON, GAIA_DRAW_MODE.SUBTRACT],
    polyline: [GAIA_DRAW_MODE.POLYLINE],
    point: [GAIA_DRAW_MODE.MARKER],
    delete: [GAIA_DRAW_MODE.DELETE],
    circle: [GAIA_DRAW_MODE.CIRCLE],
    selection: [GAIA_DRAW_MODE.SELECT, GAIA_DRAW_MODE.DESELECT],
    edit: [GAIA_DRAW_MODE.EDIT],
} as const;

const GaiaMapContent = memo(
    ({
        map,
        drawModeRef,
        points,
        featureGroup,
        tempCircle,
    }: {
        map: L.DrawMap | null;
        drawModeRef: React.MutableRefObject<GaiaDrawModeType>;
        points: React.MutableRefObject<L.LatLng[]>;
        featureGroup: React.MutableRefObject<L.FeatureGroup>;
        tempCircle: React.MutableRefObject<L.GeoJSON<any, any> | null>;
    }) => {
        const { useInitializeDrawing } = require("./hooks/useInitializeDrawing");
        useInitializeDrawing({ map, drawModeRef, points, featureGroup, tempCircle });
        return null;
    }
);

export const GaiaMap = memo((props: GaiaMapProps) => {
    const {
        tileLayer,
        onClone,
        center,
        geometryJson,
        readOnly = false,
        singleGeometryMode = false,
        enabledToolTypes,
        onChange,
        onSelect,
        clusterMarkersGroup,
        customRibbonButtons,
        disableUndoRedo = false,
        disableAcceptDrawing = false,
        colorPalette,
        wms,
        getFeatureInfo,
        onSnapshotRequest,
        onMapBoxSizeRequest,
        pointCustomIcon,
        height,
        zoom,
    } = props;
    const [mapBoxSize, setMapBoxSize] = useState<EarthlightMapBoxSize | null>(null);
    const [drawMode, setDrawMode] = useState<GaiaDrawModeType>(null);
    const internalClusterMarkersGroup = clusterMarkersGroup || {
        markers: [],
        clustered: false,
    };
    const [isFullScreen, setIsFullScreen] = useState(false);
    const showAlert = useAlertService();
    const wmsUrl = wms?.url || undefined;
    const gaiaMap = useRef<HTMLDivElement>(null);
    const points = useRef<L.LatLng[]>([]);
    const { value: printLoading, setTrue: startPrintLoading, setFalse: stopPrintLoading } = useBoolean(false);
    const featureGroup = useRef<L.FeatureGroup>(new L.FeatureGroup());
    const [workingGeometry, setWorkingGeometry] = useState<string | null>(null);
    const [wmsLayers, setWmsLayers] = useState<MapWmsLayer[]>([]);
    const [wmsLegendHeader, setWmsLegendHeader] = useState<string | null>(null);
    const [isPrintPreviewActive, setIsPrintPreviewActive] = useState(false);
    const drawModeRef = useRef(drawMode);
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useInitializeMap({ mapContainer, tileLayer, featureGroup, center, wms, setWmsLayers, setWmsLegendHeader, zoom });
    const mapExists = !!map;
    const showSnapshotButton = !!onSnapshotRequest;
    const { pushHistory, getUndoState, getRedoState, canUndo, canRedo } = useMapHistory();
    const [wmsLayer, setWmsLayer] = useState<any>(null);
    const selectedWmsLayersCount = wmsLayers.filter((l) => l.selected).length;
    const [hasGeometry, setHasGeometry] = useState(false);
    const tempCircle = useRef<L.GeoJSON<any, any> | null>(null);
    const centerMarker = useRef<L.Marker | null>(null);
    const [currentCenter, setCurrentCenter] = useState<L.LatLngExpression | null>(center || null);
    const updateWorkingGeometry = useCallback((geoJsonString: string | null) => setWorkingGeometry(geoJsonString), []);
    const featureGroupToWorkingGeometry = useFeatureGroupToWorkingGeometry(updateWorkingGeometry);
    const makeMarkerNonInteractive = useCallback(() => {
        if (!centerMarker.current) {
            return;
        }

        const markerElement = centerMarker.current.getElement();
        if (markerElement) {
            markerElement.style.pointerEvents = "none";
            markerElement.style.cursor = "default";
            markerElement.classList.add("non-interactive-marker");
        }

        centerMarker.current.options.interactive = false;
    }, []);

    const updateCenterMarker = useCallback(
        (center: L.LatLngExpression) => {
            if (!map) {
                return;
            }

            if (centerMarker.current) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }

            if (featureGroup.current.getLayers().length > 0) {
                return;
            }

            const redIcon = L.icon({
                iconUrl:
                    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNSIgaGVpZ2h0PSI0MSIgdmlld0JveD0iMCAwIDI1IDQxIj48cGF0aCBmaWxsLW9wYWNpdHk9Ii45IiBmaWxsPSIjRUUyMjIwIiBkPSJNMTIgMkM4LjE0IDIgNSA1LjE0IDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ni0zLjE0LTctNy03em0wIDlhMiAyIDAgMSAwIDAtNCAyIDIgMCAwIDAgMCA0eiIvPjwvc3ZnPg==",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                className: "center-marker non-interactive-marker",
            });

            centerMarker.current = L.marker(center, {
                icon: redIcon,
                interactive: false,
                keyboard: false,
                zIndexOffset: 1000,
            }).addTo(map);

            setTimeout(makeMarkerNonInteractive, 0);
        },
        [map, makeMarkerNonInteractive]
    );
    const handleFeatureGroupToWorkingGeometry = useCallback(
        (featureGroup: React.MutableRefObject<L.FeatureGroup>) => {
            const result = featureGroupToWorkingGeometry(featureGroup);

            if (featureGroup.current.getLayers().length === 0 && currentCenter && map) {
                updateCenterMarker(currentCenter);
            } else if (centerMarker.current && map) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }

            return result;
        },
        [featureGroupToWorkingGeometry, currentCenter, map, updateCenterMarker]
    );

    const layerOnClick = useCallback(
        (e: L.LeafletMouseEvent, layer: L.Polygon<any> | Polyline<LineString | MultiLineString, any> | Layer) => {
            L.DomEvent.stopPropagation(e.originalEvent);
            L.DomEvent.preventDefault(e.originalEvent);
            if (drawModeRef.current !== GAIA_DRAW_MODE.DELETE || !map) {
                return;
            }
            featureGroup.current.removeLayer(layer);
            map.removeLayer(layer);
            pushHistory({
                points: [...points.current],
                featureGroup: featureGroup.current,
                drawMode: drawModeRef.current,
                isComplete: true,
            });
            const newWorkingGeometry = handleFeatureGroupToWorkingGeometry(featureGroup);
            if (onChange) {
                onChange(newWorkingGeometry, null);
            }
        },
        [map, pushHistory, handleFeatureGroupToWorkingGeometry, onChange]
    );
    const { handleUndo, handleRedo } = useUndoRedo({ map, featureGroup, points, layerOnClick });
    const cancelDrawing = useCallback(() => {
        if (!map || drawMode === null) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.EDIT) {
            featureGroup.current.eachLayer((l: any) => {
                const leafletLayer = l as L.Polygon | L.Polyline;
                const ob = (leafletLayer as any)._originalBoundary;
                if (!ob) {
                    return;
                }
                try {
                    const isPolygon = leafletLayer instanceof L.Polygon;
                    const geometry: GeoJSON.Geometry = isPolygon
                        ? ({ type: "Polygon", coordinates: Array.isArray(ob[0][0]) ? ob : [ob] } as any)
                        : ({ type: "LineString", coordinates: ob } as any);
                    const wgsGeometry = GeometryProjector.projectToWGS(geometry, "EPSG:27700") as GeoJSON.Geometry;

                    if (wgsGeometry.type === "Polygon") {
                        const ring = (wgsGeometry as GeoJSON.Polygon).coordinates[0] || [];
                        const vertices = ring.map((c) => L.latLng(c[1] as number, c[0] as number));
                        if (vertices.length > 0) {
                            (leafletLayer as L.Polygon).setLatLngs([...vertices, vertices[0]] as any);
                        }
                    } else if (wgsGeometry.type === "LineString") {
                        const line = (wgsGeometry as GeoJSON.LineString).coordinates;
                        const vertices = line.map((c) => L.latLng(c[1] as number, c[0] as number));
                        (leafletLayer as L.Polyline).setLatLngs(vertices as any);
                    }

                    (leafletLayer as any)._hasPendingEdit = false;
                } catch (e) {
                    console.error("Error reverting edited geometry:", e);
                }
            });
        }

        map.eachLayer((layer: any) => {
            if (layer instanceof L.CircleMarker) {
                map.removeLayer(layer);
            }

            if (layer.options?.dashArray || layer.options?.className?.includes("temp-drawing") || (!featureGroup.current.hasLayer(layer) && layer.options?.interactive === false)) {
                map.removeLayer(layer);
            }
        });

        points.current = [];
        setDrawMode(null);
    }, [map, drawMode]);
    const onMapRequestHandler = useCallback(async () => {
        if (onMapBoxSizeRequest) {
            const size = await onMapBoxSizeRequest();
            if ("errorMsg" in size) {
                showAlert("Error requesting map box size: " + size.errorMsg, "error");
                return null;
            } else {
                setMapBoxSize(size);
                return size;
            }
        }
        return null;
    }, [onMapBoxSizeRequest, showAlert]);

    useEffect(() => {
        if (!readOnly) {
            return;
        }

        if (!map) {
            return;
        }

        if (tempCircle.current) {
            map.removeLayer(tempCircle.current);
            tempCircle.current = null;
        }

        const success = setGeometryFromGeoJSON(geometryJson);

        if (success && center) {
            map.setView(center, map.getZoom());
        }

        points.current = [];
        cancelDrawing();
        setDrawMode(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readOnly]);

    useEffect(() => {
        const checkGeometryInterval = setInterval(() => {
            const hasAnyGeometry = points.current.length > 0 || !!tempCircle.current || featureGroup.current.getLayers().length > 0;
            setHasGeometry(hasAnyGeometry);

            if (hasAnyGeometry && centerMarker.current && map) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            } else if (!hasAnyGeometry && currentCenter && map && !centerMarker.current) {
                updateCenterMarker(currentCenter);
            }
        }, 100);

        return () => clearInterval(checkGeometryInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        drawModeRef.current = drawMode;
        if (drawMode !== null && mapExists) {
            map.getContainer().style.cursor = "initial";
        }

        if (mapContainer.current) {
            if (drawMode === GAIA_DRAW_MODE.CLONE) {
                mapContainer.current.setAttribute("data-mode", "clone");
            } else {
                mapContainer.current.removeAttribute("data-mode");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawMode, mapExists]);

    const processGeoJsonLayer = useCallback(
        (map: L.DrawMap, layer: L.Layer, originalBoundary?: any) => {
            const geoJSONLayer = layer as any;
            if (!geoJSONLayer.feature || !geoJSONLayer.feature.geometry) {
                (layer as any)._originalBoundary = originalBoundary?.coordinates ? originalBoundary.coordinates : undefined;
                (layer as any)._initialBoundary = true;
                featureGroup.current.addLayer(layer);
                layer.on("click", (e: L.LeafletMouseEvent) => {
                    layerOnClick(e, layer);
                });
                layer.addTo(map);
                return;
            }
            const geometryType = geoJSONLayer.feature.geometry.type;

            if (geometryType === "MultiPolygon") {
                const multiPolygon = geoJSONLayer.feature.geometry;
                multiPolygon.coordinates.forEach((polygonCoords: number[][][], idx: number) => {
                    const polygonGeometry: Polygon = {
                        type: "Polygon",
                        coordinates: polygonCoords,
                    };

                    const polygonLayer = L.geoJSON<Polygon>(polygonGeometry, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polygon"),
                        },
                    });

                    polygonLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.coordinates ? originalBoundary.coordinates[idx] : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {}
                        }
                        (childLayer as any)._originalBoundary = ob;
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else if (geometryType === "MultiLineString") {
                const multiLineString = geoJSONLayer.feature.geometry;
                multiLineString.coordinates.forEach((lineCoords: number[][], idx: number) => {
                    const lineGeometry: LineString = {
                        type: "LineString",
                        coordinates: lineCoords,
                    };

                    const lineLayer = L.geoJSON(lineGeometry as any, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polyline"),
                        },
                    });

                    lineLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.coordinates ? originalBoundary.coordinates[idx] : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {}
                        }
                        (childLayer as any)._originalBoundary = ob;
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else if (geometryType === "GeometryCollection") {
                const collection = geoJSONLayer.feature.geometry;
                collection.geometries.forEach((geometry: any, idx: number) => {
                    const singleFeature: Feature = {
                        type: "Feature",
                        properties: { ...geoJSONLayer.feature.properties },
                        geometry: geometry,
                    };

                    const featureLayer = L.geoJSON(singleFeature as any, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polygon"),
                        },
                    });

                    featureLayer.eachLayer((childLayer) => {
                        let ob = originalBoundary?.coordinates ? originalBoundary.coordinates[idx] : undefined;
                        if (!ob) {
                            try {
                                const layerGeoJSON = (childLayer as any).toGeoJSON();
                                const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                                const projected = GeometryProjector.projectTo27700(fc);
                                ob = (projected.features[0].geometry as any).coordinates as any;
                            } catch {}
                        }
                        (childLayer as any)._originalBoundary = ob;
                        (layer as any)._initialBoundary = true;
                        featureGroup.current.addLayer(childLayer);
                        childLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, childLayer);
                        });
                        childLayer.addTo(map);
                    });
                });
            } else {
                const existing = originalBoundary?.coordinates ? originalBoundary.coordinates : undefined;
                let boundary27700 = existing;
                if (!boundary27700) {
                    try {
                        const layerGeoJSON = (layer as any).toGeoJSON();
                        const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                        const projected = GeometryProjector.projectTo27700(fc);
                        boundary27700 = (projected.features[0].geometry as any).coordinates as any;
                    } catch (e) {
                        boundary27700 = undefined;
                    }
                }
                (layer as any)._originalBoundary = boundary27700;
                (layer as any)._initialBoundary = true;
                featureGroup.current.addLayer(layer);
                layer.on("click", (e: L.LeafletMouseEvent) => {
                    layerOnClick(e, layer);
                });
                layer.addTo(map);
            }
        },
        [layerOnClick]
    );

    const acceptDrawing = useCallback(() => {
        if (!mapExists || drawMode === null) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.EDIT) {
            try {
                featureGroup.current.eachLayer((l: any) => {
                    const leafletLayer = l as L.Polygon | L.Polyline;
                    if (!(leafletLayer instanceof L.Polygon || leafletLayer instanceof L.Polyline)) {
                        return;
                    }
                    try {
                        const layerGeoJSON = (leafletLayer as any).toGeoJSON();
                        const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [layerGeoJSON] } as any;
                        const projected = GeometryProjector.projectTo27700(fc);
                        (leafletLayer as any)._originalBoundary = (projected.features[0].geometry as any).coordinates;
                        (leafletLayer as any)._hasPendingEdit = false;
                    } catch (e) {
                        console.error("Error committing edited geometry:", e);
                    }
                });

                const newWorkingGeometry = handleFeatureGroupToWorkingGeometry(featureGroup);
                if (onChange) {
                    onChange(newWorkingGeometry, null);
                }
            } finally {
                setDrawMode(null);
            }
            return;
        }

        if (points.current.length <= 3 && (drawMode === GAIA_DRAW_MODE.POLYGON || drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT)) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.CIRCLE) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.RULER) {
            return;
        }

        const coordinates = points.current.map((point) => [point.lng, point.lat]);
        const isPolygonMode = drawMode === GAIA_DRAW_MODE.POLYGON || drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT;
        const geometry: GeoJSON.Geometry = {
            type: isPolygonMode ? ("Polygon" as const) : ("LineString" as const as any),
            coordinates: isPolygonMode ? [coordinates] : (coordinates as any),
        };
        const geoJsonData: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {
            type: "Feature" as const,
            properties: {},
            geometry: geometry,
        };

        if (drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT) {
            if (onSelect) {
                const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [geoJsonData] };
                const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                onSelect(reprojectedGeometry.features[0] as GeoJSON.Feature<GeoJSON.Polygon>, drawMode === GAIA_DRAW_MODE.SELECT ? "select" : "deselect");
            }
        } else {
            const geoJsonLayer = L.geoJSON(geoJsonData, {
                style: {
                    ...MapViewHelper.getLayerStyling(drawMode === GAIA_DRAW_MODE.POLYGON ? "polygon" : "polyline"),
                    className: drawMode === GAIA_DRAW_MODE.POLYGON ? "accepted-polygon" : "",
                },
            });
            geoJsonLayer.eachLayer((layer) => processGeoJsonLayer(map, layer));
        }

        map.eachLayer((layer: any) => {
            if (layer instanceof L.Polyline && (layer.options?.dashArray || layer.options?.className?.includes("temp-drawing"))) {
                map.removeLayer(layer);
            }
            if (layer instanceof L.CircleMarker) {
                map.removeLayer(layer);
            }
        });

        pushHistory(
            {
                points: [],
                featureGroup: featureGroup.current,
                drawMode: null,
                isComplete: drawMode === GAIA_DRAW_MODE.POLYGON,
            },
            false
        );

        if (drawMode !== GAIA_DRAW_MODE.SELECT && drawMode !== GAIA_DRAW_MODE.DESELECT) {
            const newWorkingGeometry = handleFeatureGroupToWorkingGeometry(featureGroup);
            if (onChange) {
                const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [geoJsonData] };
                const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                onChange(newWorkingGeometry, JSON.stringify(reprojectedGeometry.features[0]));
            }
        }

        points.current = [];
        setDrawMode(null);
    }, [mapExists, drawMode, map, pushHistory, featureGroupToWorkingGeometry, onChange, processGeoJsonLayer, onSelect]);

    const centerMap = useCallback(
        (newCenter: L.LatLngExpression | null) => {
            if (!mapExists) {
                return;
            }
            if (newCenter) {
                map.setView(newCenter, map.getZoom());
                setCurrentCenter(newCenter);
            } else if (featureGroup.current.getLayers().length > 0) {
                try {
                    const bounds = featureGroup.current.getBounds();
                    if (bounds.isValid()) {
                        map.fitBounds(bounds, { padding: [20, 20] });
                    }
                } catch (error) {
                    showAlert("Error centering map on geometry", "error");
                    console.error("Error centering map on geometry:", error);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mapExists]
    );

    const setGeometryFromGeoJSON = useCallback(
        (geoJsonString: string | null | undefined): boolean => {
            if (!mapExists) {
                return false;
            }

            if (centerMarker.current) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }

            if (!geoJsonString) {
                featureGroup.current.eachLayer((layer) => map.removeLayer(layer));
                featureGroup.current.clearLayers();
                updateWorkingGeometry(geoJsonString || "");
                pushHistory(
                    {
                        points: [],
                        featureGroup: featureGroup.current,
                        drawMode: null,
                        isComplete: true,
                    },
                    false,
                    true
                );

                if (currentCenter) {
                    updateCenterMarker(currentCenter);
                }

                return true;
            }

            try {
                const geoJsonData = JSON.parse(geoJsonString);
                const projected = GeometryProjector.projectToWGS(geoJsonData);
                const geoJsonLayer = L.geoJSON(projected, {
                    style: {
                        ...MapViewHelper.getLayerStyling("polygon"),
                    },
                });
                featureGroup.current.eachLayer((layer) => map.removeLayer(layer));
                featureGroup.current.clearLayers();

                geoJsonLayer.eachLayer((layer) => processGeoJsonLayer(map, layer, geoJsonData));

                featureGroupToWorkingGeometry(featureGroup);

                try {
                    const bounds = featureGroup.current.getBounds();
                    if (bounds.isValid()) {
                        map.fitBounds(bounds, { padding: [20, 20] });
                    }
                } catch (error) {
                    showAlert("Error centering map on imported geometry", "error");
                    console.error("Error centering map on imported geometry:", error);
                }

                pushHistory(
                    {
                        points: [],
                        featureGroup: featureGroup.current,
                        drawMode: null,
                        isComplete: true,
                    },
                    false,
                    true
                );

                return true;
            } catch (error) {
                showAlert("Error setting geometry from GeoJSON", "error");
                console.error("Error setting geometry from GeoJSON:", error);

                return false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layerOnClick, mapExists, pushHistory, updateWorkingGeometry]
    );

    useEffect(() => {
        if (!mapExists || geometryJson === workingGeometry) {
            return;
        }

        const success = setGeometryFromGeoJSON(geometryJson);

        if (success && center) {
            map.setView(center, map.getZoom());
            if (!geometryJson) {
                updateCenterMarker(center);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapExists, geometryJson]);

    useEffect(() => {
        if (!mapExists) {
            return;
        }
        if (center) {
            map.setView(center, map.getZoom());

            if (featureGroup.current.getLayers().length === 0) {
                updateCenterMarker(center);
            } else if (centerMarker.current) {
                map.removeLayer(centerMarker.current);
                centerMarker.current = null;
            }
        } else if (featureGroup.current.getLayers().length > 0) {
            try {
                const bounds = featureGroup.current.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] });
                }
            } catch (error) {
                console.error("Error centering map on geometry:", error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapExists, center]);

    const { theme } = useGaiaMapTheme();

    const onLegendCheckboxClicked = useCallback((e: MirageOnChangeEvent, value: any, layer: MapWmsLayer): void => {
        e.stopPropagation();
        setWmsLayers((prev) => {
            const newWmsLayers = [...prev];
            const index = newWmsLayers.findIndex((l) => l.layer.name === layer.layer.name);
            if (index !== -1) {
                newWmsLayers[index].selected = value;
            }
            return newWmsLayers;
        });
    }, []);
    const getWmsLegendItems = useCallback(
        (layers: MapWmsLayer[]) => {
            return layers.map((r) => {
                return (
                    <Field
                        containerClassName="mapView__legendField"
                        key={r.layer.name}
                        value={r.selected}
                        contentType="CHECKBOX"
                        label={r.layer.title}
                        onChange={(e, v) => onLegendCheckboxClicked(e, v, r)}
                        mode={"edit"}
                    />
                );
            });
        },
        [onLegendCheckboxClicked]
    );
    const wmsLayerFields: JSX.Element[] = wms ? (getWmsLegendItems(wmsLayers).filter((f) => f !== null) as JSX.Element[]) : [];
    const legend =
        wms && wms.showLegend ? <Legend map={map} header={wmsLegendHeader ? <Typography variant="h6">{wmsLegendHeader}</Typography> : null} rows={wmsLayerFields} /> : null;

    useEffect(() => {
        if (!wms) {
            return;
        }
        const showSelectedWmsLayers = () => {
            const layersArray = wmsLayers.filter((l) => l.selected).map((l) => l.layer.name);
            const layersString = layersArray.join(",");
            if (!map || !wms?.url) {
                return layersArray;
            }
            if (wmsLayer) {
                wmsLayer.options.layers = layersString;
                wmsLayer.initialize(wms.url, wmsLayer.options);
                wmsLayer.redraw();
            } else {
                const nonTitled = (L as any).nonTiledLayer as any;
                const newWmsLayer = new nonTitled.WMS(wms.url as string, {
                    layers: layersString,
                    opacity: 1.0,
                    format: "image/png",
                    transparent: true,
                    zIndex: 5,
                    attribution: "",
                });
                newWmsLayer.addTo(map);
                setWmsLayer(newWmsLayer);
            }
            return layersArray;
        };
        const lrs = showSelectedWmsLayers();
        wms.onShownLayersChanged?.(lrs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedWmsLayersCount]);

    useEffect(() => {
        MapViewHelper.setColorPalette(colorPalette || theme.palette || null);
    }, [colorPalette, theme.palette]);

    const activatePrintPreview = useCallback(() => {
        setIsPrintPreviewActive(true);
    }, []);

    const closePrintPreview = useCallback(() => {
        setIsPrintPreviewActive(false);
    }, []);

    const onSnapshotRequested = useCallback(
        async (req: PrintMapRequest) => {
            if (onSnapshotRequest) {
                await onSnapshotRequest(req);
            }
        },
        [onSnapshotRequest]
    );

    const contextValue = useMemo<GaiaMapContext>(
        () => ({
            setDrawMode,
            drawMode,
            canUndo,
            canRedo,
            onUndo: handleUndo,
            onRedo: handleRedo,
            pushHistory,
            getUndoState,
            getRedoState,
            acceptDrawing,
            cancelDrawing,
            hasGeometry,
            readOnly,
            singleGeometryMode,
            onClone,
            center: currentCenter,
            centerMap,
            setGeometryFromGeoJSON,
            layerOnClick,
            workingGeometry,
            updateWorkingGeometry,
            featureGroupToWorkingGeometry,
            enabledToolTypes,
            onChange,
            disableUndoRedo,
            disableAcceptDrawing,
            onSelect,
            getFeatureInfo,
            wmsLayers,
            wmsUrl,
            activatePrintPreview,
            printLoading,
            startPrintLoading,
            stopPrintLoading,
            onMapBoxSizeRequested: onMapRequestHandler,
            mapBoxSize,
            pointCustomIcon,
            isFullScreen,
            setIsFullScreen,
        }),
        [
            drawMode,
            canUndo,
            canRedo,
            handleUndo,
            handleRedo,
            pushHistory,
            getUndoState,
            getRedoState,
            acceptDrawing,
            cancelDrawing,
            hasGeometry,
            readOnly,
            singleGeometryMode,
            onClone,
            currentCenter,
            centerMap,
            setGeometryFromGeoJSON,
            layerOnClick,
            workingGeometry,
            updateWorkingGeometry,
            featureGroupToWorkingGeometry,
            enabledToolTypes,
            onChange,
            disableUndoRedo,
            disableAcceptDrawing,
            onSelect,
            getFeatureInfo,
            wmsLayers,
            wmsUrl,
            activatePrintPreview,
            printLoading,
            startPrintLoading,
            stopPrintLoading,
            onMapRequestHandler,
            mapBoxSize,
            pointCustomIcon,
            isFullScreen,
            setIsFullScreen,
        ]
    );

    return (
        <GaiaMapProvider value={contextValue}>
            <Box className="gaia__container" ref={gaiaMap} style={{ height: isFullScreen ? undefined : height }}>
                <GaiaMapRibbon customRibbonButtons={customRibbonButtons} showSnapshotButton={showSnapshotButton} />
                <Box className="gaia__map__container" style={{ position: "relative" }}>
                    <Box ref={mapContainer} className="gaia__map" style={{ height: isFullScreen ? undefined : `calc(${height}px - 80px)` }} />
                    <GaiaMapControls map={map} />
                    <GaiaMapPrintPreview isActive={isPrintPreviewActive} onClose={closePrintPreview} map={map} onSnapshotRequested={onSnapshotRequested} />
                </Box>
            </Box>
            <GaiaMapContent map={map} drawModeRef={drawModeRef} points={points} featureGroup={featureGroup} tempCircle={tempCircle} />
            {legend}
            {map && <MapMarkersPane {...internalClusterMarkersGroup} map={map} />}
        </GaiaMapProvider>
    );
});
